import React, { useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import Button from '../Button';
import RangePicker from './RangePicker';
import DateSelector from './DateSelector';
import DayRow from './DayRow';

function DatePicker({
  date,
  setDate,
  onCancel,
  onSubmit,
  marked,
  className,
}) {
  const [selectedMonth, setSelectedMonth] = useState(new Date(date).getMonth());
  const [selectedDay, setSelectedDay] = useState(new Date(date).getDate());
  const [selectedYear, setSelectedYear] = useState(new Date(date).getFullYear());

  const [showingMonth, setShowingMonth] = useState(selectedMonth);
  const [showingYear, setShowingYear] = useState(selectedYear);

  function setSelectedDate(day, month, year) {
    setSelectedDay(day);
    setSelectedMonth(month);
    setSelectedYear(year);
    setDate(new Date(year, month, day));
  }

  const showDate = showingYear === selectedYear && showingMonth === selectedMonth;

  const handleCancel = () => onCancel();
  const handleSubmit = () => onSubmit();

  return (
    <div className={classNames('date-picker-container', className)}>
      <div className="date-picker">
        <DateSelector
          month={showingMonth}
          year={showingYear}
          setMonth={setShowingMonth}
          setYear={setShowingYear}
        />
        <DayRow />
        <DateRow
          day={showDate && selectedDay}
          month={showingMonth}
          year={showingYear}
          setSelectedDate={setSelectedDate}
          setShowingMonth={setShowingMonth}
          setShowingYear={setShowingYear}
          marked={marked}
        />
      </div>
      <div className="date-picker-footer">
        <Button
          variant="outline-gray"
          onClick={handleCancel}
          size="sm"
          className="date-picker-footer-button"
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          size="sm"
          className="date-picker-footer-button"
        >
          Apply
        </Button>
      </div>
    </div>
  );
}

DatePicker.propTypes = {
  date: PropTypes.string,
  setDate: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  marked: PropTypes.string,
  className: PropTypes.string,
};

DatePicker.defaultProps = {
  date: '',
  marked: '',
  className: '',
};

function DateRow({
  month,
  day,
  setSelectedDate,
  year,
  setShowingMonth,
  setShowingYear,
  marked,
}) {
  const markedDay = new Date(marked).getDate();
  const markedMonth = new Date(marked).getMonth();
  const markedYear = new Date(marked).getFullYear();

  const daysInCurrentMonth = new Date(year, month + 1, 0).getDate();
  let firstDayOfCurrentMonth = new Date(year, month, 1).getDay();
  firstDayOfCurrentMonth = firstDayOfCurrentMonth === 0 ? 6 : firstDayOfCurrentMonth - 1;

  const daysInPreviousMonth = new Date(year, month, 0).getDate();
  const totalCells = firstDayOfCurrentMonth
    + daysInCurrentMonth
    + (7 - ((daysInCurrentMonth + firstDayOfCurrentMonth) % 7));
  const daysInNextMonth = totalCells - daysInCurrentMonth - firstDayOfCurrentMonth;

  const prevMonthDates = Array.from(
    { length: firstDayOfCurrentMonth },
    (_, i) => daysInPreviousMonth - firstDayOfCurrentMonth + i + 1
  );
  const currentMonthDates = Array.from(
    { length: daysInCurrentMonth },
    (_, i) => i + 1
  );
  const nextMonthDates = Array.from(
    { length: daysInNextMonth },
    (_, i) => i + 1
  );

  return (
    <div className="date-row">
      {prevMonthDates.map((date) => {
        const isMarked = date === markedDay && month - 1 === markedMonth && year === markedYear;
        return (
          <div
            className={classNames('date-cell prev-month', { 'date-marked': isMarked })}
            key={`prev-${date}`}
            onClick={() => {
              setSelectedDate(date, month - 1, year);
              setShowingMonth(month - 1);
              if (month === 0) {
                setShowingYear(year - 1);
              }
            }}
            role="button"
            tabIndex={0}
            onKeyPress={() => {
              setSelectedDate(date, month - 1, year);
              setShowingMonth(month - 1);
              if (month === 0) {
                setShowingYear(year - 1);
              }
            }}
          >
            {date}
            {isMarked && <div className="date-mark" />}
          </div>
        );
      })}
      {currentMonthDates.map((date) => {
        const isMarked = date === markedDay && month === markedMonth && year === markedYear;
        return (
          <div
            className={classNames('date-cell', { 'date-selected': date === day }, { 'date-marked': isMarked })}
            key={date}
            onClick={() => setSelectedDate(date, month, year)}
            role="button"
            tabIndex={0}
            onKeyPress={() => setSelectedDate(date, month, year)}
          >
            {date}
            <div className="date-mark" />
          </div>
        );
      })}
      {nextMonthDates.map((date) => {
        const isMarked = date === markedDay && month + 1 === markedMonth && year === markedYear;
        return (
          <div
            className={classNames('date-cell next-month', { 'date-marked': isMarked })}
            key={`next-${date}`}
            onClick={() => {
              setSelectedDate(date, month + 1, year);
              setShowingMonth(month + 1);
              if (month === 11) {
                setShowingYear(year + 1);
              }
            }}
            role="button"
            tabIndex={0}
            onKeyPress={() => {
              setSelectedDate(date, month + 1, year);
              setShowingMonth(month + 1);
              if (month === 11) {
                setShowingYear(year + 1);
              }
            }}
          >
            {date}
            {isMarked && <div className="date-mark" />}
          </div>
        );
      })}
    </div>
  );
}

DateRow.propTypes = {
  month: PropTypes.number,
  day: PropTypes.number,
  setSelectedDate: PropTypes.func.isRequired,
  year: PropTypes.number.isRequired,
  setShowingMonth: PropTypes.func.isRequired,
  setShowingYear: PropTypes.func.isRequired,
  marked: PropTypes.string,
};

DateRow.defaultProps = {
  month: new Date().getMonth(),
  day: new Date().getDate(),
  marked: '',
};

export default DatePicker;
export { RangePicker };
